import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Box, Checkbox, Dialog, DialogTitle, Drawer, IconButton, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import SendIcon from "@mui/icons-material/Send";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import ZoomOutOutlinedIcon from "@mui/icons-material/ZoomOutOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import ReactMarkdown from "react-markdown";
import i18next from "i18next";
import { BRAND, FONT, RADIUS, SHADOW, DOC_STATUS, MEDIA } from "./lightTheme";
import { getEnv } from "config/Config";
import { Context } from "states/Store";
import { RoleBasedAccessControlContext, PrivilegeEnum } from "services/common/RolesUtils";
import { getDoctypeTranslatedName } from "services/common/DocumentUtils";
import { formatDt } from "./utils/lightUtils";
import useMentionAutocomplete from "./hooks/useMentionAutocomplete";
import { getAccountUsers } from "services/edge/UserService";
import {
  getEvidenceDetail,
  getEvidenceFile,
  getFileStatus,
  getEvidenceExtractionDetail,
  getEvidenceSmartControlDetail,
  getEvidenceMultiple,
} from "services/edge/EvidenceService";
import { getDocumentHistoric } from "services/edge/DossierService";
import {
  getAllNoteDocument,
  createNoteDocument,
  deleteNoteDocument,
} from "services/edge/NoteService";
import { approveDocument, rejectDocument } from "services/edge/DocumentService";
import { getApiErrorMessageCode } from "services/common/ApiUtils";
import { ERROR, SUCCESS } from "services/common/Constants";

// Worker copié dans public/ pour contourner le bundling webpack qui casse en prod (require is not defined).
// À re-synchroniser si react-pdf/pdfjs-dist est mis à jour (source : node_modules/pdfjs-dist/build/pdf.worker.min.js).
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

const STATUS_MAP = DOC_STATUS;

const TABS = [
  { id: "info",      label: () => i18next.t("light.docViewer.info"),       icon: InfoOutlinedIcon },
  { id: "answers",   label: () => i18next.t("light.docViewer.answers"),     icon: FormatListBulletedOutlinedIcon },
  { id: "history",   label: () => i18next.t("light.docViewer.history"),         icon: HistoryOutlinedIcon },
  { id: "notes",     label: () => i18next.t("light.detail.notes"),              icon: ChatBubbleOutlineIcon },
];

// ─── Styles ────────────────────────────────────────────────────────────────────
const useStyles = makeStyles()(() => ({
  drawerPaper: {
    width: "92vw", maxWidth: "1400px",
    display: "flex", flexDirection: "column",
    backgroundColor: BRAND.background, fontFamily: FONT.family,
    [MEDIA.smDown]: { width: "100vw", maxWidth: "100vw" },
  },

  // ── Header ──
  header: {
    display: "flex", alignItems: "center", gap: "0.75rem",
    padding: "1.1rem 1.5rem", backgroundColor: BRAND.primary, flexShrink: 0,
    [MEDIA.smDown]: { padding: "0.85rem 1rem", gap: "0.5rem" },
  },

  // ── Mobile top switcher (Document | Infos) ──
  mobileSwitcher: {
    display: "none",
    [MEDIA.smDown]: {
      display: "flex", gap: "0.4rem",
      padding: "0.55rem 0.75rem",
      backgroundColor: BRAND.white,
      borderBottom: `1px solid ${BRAND.border}`,
      flexShrink: 0,
    },
  },
  mobileSwitcherBtn: {
    flex: 1, padding: "0.5rem 0.75rem", borderRadius: RADIUS.md,
    border: `1.5px solid ${BRAND.border}`, backgroundColor: BRAND.white,
    ...FONT.body, fontFamily: FONT.family, color: BRAND.textMuted, fontWeight: 600,
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem",
    transition: "background 0.15s, color 0.15s, border-color 0.15s",
  },
  mobileSwitcherBtnActive: {
    backgroundColor: BRAND.primary, color: `${BRAND.white} !important`,
    borderColor: `${BRAND.primary} !important`,
  },
  // Utility: hide only on mobile (keeps desktop untouched)
  mobileHidden: {
    [MEDIA.smDown]: { display: "none !important" },
  },
  headerIcon: {
    width: "36px", height: "36px", borderRadius: RADIUS.md,
    backgroundColor: "rgba(255,255,255,0.12)",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  headerTitle: {
    ...FONT.h2, color: BRAND.white, fontFamily: FONT.family,
    flex: 1, minWidth: 0,
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  },
  headerStatus: {
    display: "inline-flex", alignItems: "center",
    borderRadius: "20px", padding: "4px 12px",
    ...FONT.small, fontWeight: 700, flexShrink: 0, fontFamily: FONT.family,
  },
  closeBtn: {
    width: "32px", height: "32px", borderRadius: RADIUS.sm,
    border: "none", background: "rgba(255,255,255,0.12)",
    color: BRAND.white, cursor: "pointer", display: "flex",
    alignItems: "center", justifyContent: "center",
    flexShrink: 0, transition: "background 0.15s",
    "&:hover": { background: "rgba(255,255,255,0.22)" },
  },

  // ── Body ──
  body: {
    display: "flex", flex: 1, overflow: "hidden",
    [MEDIA.smDown]: { flexDirection: "column" },
  },

  // ── Left sidebar ──
  sidebar: {
    width: "560px", flexShrink: 0,
    display: "flex", flexDirection: "column",
    borderRight: `1px solid ${BRAND.border}`,
    backgroundColor: BRAND.white, overflow: "hidden",
    [MEDIA.smDown]: {
      width: "100%", flexShrink: 1, borderRight: "none",
      borderBottom: `1px solid ${BRAND.border}`,
    },
  },

  // ── Tab bar ──
  tabBar: {
    display: "flex", flexDirection: "column", gap: "2px",
    padding: "0.75rem", borderBottom: `1px solid ${BRAND.border}`,
    backgroundColor: BRAND.background, flexShrink: 0,
  },
  tab: {
    display: "flex", alignItems: "center", gap: "0.5rem",
    padding: "0.5rem 0.75rem", borderRadius: RADIUS.sm,
    cursor: "pointer", border: "none", background: "transparent",
    ...FONT.body, fontFamily: FONT.family, color: BRAND.textMuted,
    transition: "background 0.12s, color 0.12s",
    textAlign: "left", width: "100%",
    "&:hover": { background: BRAND.border, color: BRAND.textMain },
  },
  tabActive: {
    backgroundColor: `${BRAND.primary}18`, color: BRAND.primary, fontWeight: 700,
    "&:hover": { backgroundColor: `${BRAND.primary}22` },
  },
  tabBadge: {
    marginLeft: "auto", backgroundColor: BRAND.border, color: BRAND.textMuted,
    borderRadius: "20px", padding: "1px 6px",
    ...FONT.meta, fontFamily: FONT.family,
  },
  tabBadgeActive: { backgroundColor: `${BRAND.primary}30`, color: BRAND.primary },

  // ── Tab content ──
  tabContent: { flex: 1, overflowY: "auto", padding: "1rem 1.25rem" },

  // ── PDF pane ──
  pdfPane: {
    flex: 1, display: "flex", flexDirection: "column",
    overflow: "hidden", backgroundColor: "#E8E8E8", position: "relative",
  },
  pdfToolbar: {
    display: "flex", alignItems: "center", gap: "0.25rem",
    padding: "0.4rem 0.75rem", backgroundColor: "#404040",
    flexShrink: 0, borderBottom: "1px solid #333",
  },
  pdfToolbarGroup: { display: "flex", alignItems: "center", gap: "0.25rem" },
  pdfToolbarDivider: {
    width: "1px", height: "18px",
    backgroundColor: "rgba(255,255,255,0.15)", margin: "0 0.35rem",
  },
  pdfToolbarSpacer: { flex: 1 },
  pdfBtn: {
    height: "30px", minWidth: "30px", paddingInline: "0.5rem",
    borderRadius: RADIUS.sm, border: "none", backgroundColor: "transparent",
    color: "rgba(255,255,255,0.8)", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", gap: "0.3rem",
    ...FONT.small, fontFamily: FONT.family,
    transition: "background 0.12s, color 0.12s",
    "&:hover": { backgroundColor: "rgba(255,255,255,0.12)", color: "#fff" },
    "&:disabled": { opacity: 0.35, cursor: "not-allowed" },
  },
  pdfPageInfo: {
    ...FONT.small, color: "rgba(255,255,255,0.7)",
    fontFamily: FONT.family, padding: "0 0.25rem", userSelect: "none",
  },
  pdfScroll: {
    flex: 1, overflowY: "auto",
    display: "flex", flexDirection: "column",
    alignItems: "center", padding: "1.5rem 1rem", gap: "1rem",
  },
  pdfPlaceholder: {
    flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
    color: "#888", fontFamily: FONT.family, ...FONT.body,
    flexDirection: "column", gap: "0.75rem",
  },

  // ── Generic blocks ──
  sectionTitle: {
    ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family,
    marginBottom: "0.65rem", marginTop: "1rem",
    "&:first-of-type": { marginTop: 0 },
  },
  metaRow: {
    display: "flex", alignItems: "flex-start", gap: "0.5rem",
    marginBottom: "0.55rem",
    "&:last-child": { marginBottom: 0 },
  },
  metaLabel: {
    ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family,
    minWidth: "90px", flexShrink: 0,
    display: "flex", alignItems: "center", gap: "4px",
  },
  metaValue: {
    ...FONT.body, fontWeight: 600, color: BRAND.textMain,
    fontFamily: FONT.family, flex: 1, wordBreak: "break-word",
  },

  // ── Status block ──
  statusBlock: { borderRadius: RADIUS.md, padding: "0.75rem 1rem", marginBottom: "0.75rem" },
  statusLabel: { ...FONT.small, fontWeight: 700, fontFamily: FONT.family, marginBottom: "0.2rem" },
  statusDetail: { ...FONT.small, fontFamily: FONT.family, opacity: 0.85 },

  // ── History ──
  historyTimeline: { display: "flex", flexDirection: "column" },
  historyItem: { display: "flex", flexDirection: "row", alignItems: "flex-start" },
  historyDot: {
    width: "10px", height: "10px", borderRadius: "50%",
    backgroundColor: BRAND.border, border: `2px solid ${BRAND.white}`,
    boxSizing: "border-box", flexShrink: 0, outline: `1px solid ${BRAND.border}`,
  },
  historyDepositor: { ...FONT.body, fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family },
  historyDate: { ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family, marginTop: "1px" },
  historyRange: { ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family, marginTop: "1px" },

  // ── Collected data ──
  collectedRow: {
    display: "flex", flexDirection: "column", gap: "0.2rem",
    marginBottom: "0.65rem", "&:last-child": { marginBottom: 0 },
  },
  collectedKey: { ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family },
  collectedVal: { ...FONT.body, color: BRAND.textMain, fontFamily: FONT.family, lineHeight: 1.4 },
  summaryBox: {
    backgroundColor: BRAND.background, borderRadius: RADIUS.sm,
    padding: "0.75rem 1rem", ...FONT.body, color: BRAND.textMain,
    fontFamily: FONT.family, lineHeight: 1.6,
    "& p": { margin: "0 0 0.5rem", "&:last-child": { marginBottom: 0 } },
    "& strong": { fontWeight: 700 },
    "& ul, & ol": { paddingLeft: "1.2rem", margin: "0 0 0.5rem" },
    "& li": { marginBottom: "0.2rem" },
    "& h1, & h2, & h3": { fontWeight: 700, marginBottom: "0.35rem", ...FONT.body },
  },

  // ── Notes ──
  noteInput: { display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "0.75rem" },
  noteTextarea: {
    flex: 1, borderRadius: RADIUS.sm,
    border: `1.5px solid ${BRAND.border}`,
    padding: "0.5rem 0.75rem", fontFamily: FONT.family,
    ...FONT.body, resize: "none", outline: "none",
    backgroundColor: BRAND.background, color: BRAND.textMain,
    lineHeight: 1.4, minHeight: "52px",
    "&:focus": { borderColor: BRAND.primary },
  },
  noteSendBtn: {
    width: "34px", height: "34px", borderRadius: RADIUS.sm,
    border: "none", backgroundColor: BRAND.primary, color: BRAND.white,
    cursor: "pointer", display: "flex", alignItems: "center",
    justifyContent: "center", flexShrink: 0, transition: "opacity 0.15s",
    "&:hover": { opacity: 0.88 },
    "&:disabled": { opacity: 0.35, cursor: "not-allowed" },
  },
  noteItem: {
    padding: "0.75rem 0", borderBottom: `1px solid ${BRAND.border}`,
    "&:last-child": { borderBottom: "none" },
  },
  noteHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.2rem" },
  noteAuthor: { ...FONT.small, fontWeight: 700, color: BRAND.textMain, fontFamily: FONT.family },
  noteDate: { ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family },
  noteText: { ...FONT.body, color: BRAND.textMain, fontFamily: FONT.family, lineHeight: 1.5, whiteSpace: "pre-wrap" },
  noteDeleteBtn: {
    padding: "2px", border: "none", background: "transparent",
    cursor: "pointer", color: BRAND.textMuted, borderRadius: RADIUS.sm,
    display: "flex", transition: "color 0.15s",
    "&:hover": { color: "#E84343" },
  },
  emptyState: {
    padding: "1.5rem 0", textAlign: "center",
    color: BRAND.textMuted, fontFamily: FONT.family, ...FONT.body,
  },
  // ── @mention ──
  mentionWrap: { position: "relative" },
  mentionDropdown: {
    position: "absolute", zIndex: 300,
    top: "100%", left: 0, right: 0, marginTop: "3px",
    backgroundColor: BRAND.white, border: `1.5px solid ${BRAND.border}`,
    borderRadius: RADIUS.md, boxShadow: SHADOW.strong,
    overflow: "hidden", maxHeight: "160px", overflowY: "auto",
  },
  mentionItem: {
    display: "flex", alignItems: "center", gap: "0.5rem",
    padding: "0.4rem 0.75rem", cursor: "pointer",
    transition: "background 0.1s",
    "&:hover": { backgroundColor: BRAND.background },
  },
  mentionItemActive: { backgroundColor: BRAND.background },
  mentionAvatar: {
    width: "24px", height: "24px", borderRadius: "50%",
    backgroundColor: BRAND.primary, color: BRAND.white,
    display: "flex", alignItems: "center", justifyContent: "center",
    ...FONT.tiny, fontWeight: 700, flexShrink: 0,
  },
  mentionName: { ...FONT.body, fontWeight: 500, color: BRAND.textMain, fontFamily: FONT.family },
  loading: { padding: "2rem", textAlign: "center", color: BRAND.textMuted, fontFamily: FONT.family, ...FONT.body },
  // ── SmartCheck ──
  scResultRow: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    gap: "0.5rem", padding: "0.5rem 0.75rem",
    backgroundColor: BRAND.background, borderRadius: RADIUS.sm, marginTop: "0.5rem",
  },
  scResultLabel: { ...FONT.small, fontWeight: 600, color: BRAND.textMuted, fontFamily: FONT.family, flexShrink: 0 },
  scResultValue: { ...FONT.body, fontWeight: 600, fontFamily: FONT.family, textAlign: "right" },
  scSubtitle: { ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.25rem" },
  scExtractRow: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    gap: "0.5rem", padding: "0.3rem 0", borderBottom: `1px solid ${BRAND.border}`,
  },
  scExtractLabel: { ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family, flex: 1 },
  scExtractValue: { ...FONT.small, fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family, textAlign: "right", flex: 1 },
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatIssuer(issuer, fiscaleMereFille) {
  if (!issuer) return "";
  let label = i18next.t(`viewer.issuer.${issuer}`, issuer);
  if (fiscaleMereFille) label += ` ${i18next.t("viewer.fiscale_mere_fille", "(attestation fiscale mère/fille)")}`;
  return label;
}


// ─── Shared Q/A row (matches TabAnswers style) ────────────────────────────────
const QARow = ({ label, children }) => (
  <Box style={{ marginBottom: "0.75rem", borderBottom: `1px solid ${BRAND.border}`, paddingBottom: "0.75rem" }}>
    <Typography style={{ ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.35rem" }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const QAPill = ({ value, color }) => (
  <Box style={{ display: "inline-flex" }}>
    <Typography style={{ fontSize: "0.82rem", fontWeight: 700, color: color ?? BRAND.textMain, fontFamily: "Inter", backgroundColor: BRAND.background, borderRadius: "6px", padding: "2px 10px", border: `1px solid ${BRAND.border}` }}>
      {value ?? "—"}
    </Typography>
  </Box>
);

// ─── SmartCheck helpers ────────────────────────────────────────────────────────
const INFO_ROW = ({ label, value, valueColor }) => (
  <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.35rem 0", borderBottom: `1px solid ${BRAND.border}` }}>
    <Typography style={{ fontSize: "0.78rem", color: BRAND.textMuted, fontFamily: "Inter" }}>{label}</Typography>
    <Typography style={{ fontSize: "0.78rem", fontWeight: 600, color: valueColor ?? BRAND.textMain, fontFamily: "Inter", textAlign: "right", maxWidth: "60%" }}>{value ?? "—"}</Typography>
  </Box>
);

const SC_CHECK = ({ ok, label, sub = false }) => (
  <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.35rem 0", borderBottom: `1px solid ${BRAND.border}`, paddingLeft: sub ? "1rem" : 0 }}>
    <Typography style={{ fontSize: sub ? "0.73rem" : "0.78rem", color: sub ? BRAND.textMuted : BRAND.textMain, fontFamily: "Inter" }}>{label}</Typography>
    <Box style={{ width: "18px", height: "18px", borderRadius: "50%", backgroundColor: ok ? "#DCFCE7" : "#FEE2E2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Typography style={{ fontSize: "0.65rem", fontWeight: 800, color: ok ? "#15803D" : "#B91C1C", lineHeight: 1 }}>{ok ? "✓" : "✗"}</Typography>
    </Box>
  </Box>
);

function SmartCheckSection({ sc, classes: s }) {
  if (!sc) return null;
  const rr = sc.receivabilityResult;
  const isV2 = rr && ("right_document" in rr || "errors" in rr);

  // Résultat global
  const globalOk = sc.receivability === true;
  const globalMsg = globalOk
    ? i18next.t("light.docViewer.documentChecked")
    : (isV2 ? (rr?.global_error ?? rr?.errors?.bad_document_error) : rr?.final_error_message) ?? i18next.t("light.docViewer.checkInconclusive");

  return (
    <>
      <Typography className={s.sectionTitle}>{i18next.t("light.docViewer.smartCheck")}</Typography>

      {/* Résultat */}
      <QARow label={i18next.t("light.docViewer.smartCheckResult")}>
        <QAPill value={globalMsg} color={globalOk ? "#15803D" : "#B91C1C"} />
      </QARow>

      {/* Détails V2 */}
      {isV2 && rr && (
        <>
          <Typography className={s.sectionTitle}>{i18next.t("light.docViewer.details")}</Typography>
          {rr.right_document != null && (
            <SC_CHECK ok={rr.right_document} label={i18next.t("light.docViewer.sc.contentCheck")} />
          )}
          {rr.company_informations != null && (
            <SC_CHECK ok={rr.company_informations.found !== false} label={i18next.t("light.docViewer.sc.companyInfo")} />
          )}
          {rr.company_informations?.identifier != null && (
            <SC_CHECK sub ok={rr.company_informations.identifier.found !== false} label={i18next.t("light.docViewer.sc.identifiers")} />
          )}
          {rr.company_informations?.siren != null && (
            <SC_CHECK sub ok={rr.company_informations.siren.found !== false} label="SIREN" />
          )}
          {rr.company_informations?.siret != null && (
            <SC_CHECK sub ok={rr.company_informations.siret.found !== false} label="SIRET" />
          )}
          {rr.company_informations?.tva != null && (
            <SC_CHECK sub ok={rr.company_informations.tva.found !== false} label="TVA" />
          )}
          {rr.company_informations?.company_name != null && (
            <SC_CHECK sub ok={rr.company_informations.company_name.found !== false} label={i18next.t("light.docViewer.sc.companyName")} />
          )}
          {rr.expiration != null && (
            <SC_CHECK ok={!rr.expiration.is_expired} label={i18next.t("light.docViewer.sc.dateCheck")} />
          )}
          {rr.iban_found === false && (
            <SC_CHECK ok={false} label="IBAN" />
          )}
        </>
      )}

      {/* Détails V1 */}
      {!isV2 && rr && (
        <>
          <Typography className={s.sectionTitle}>{i18next.t("light.docViewer.details")}</Typography>
          {rr.company_informations != null && (
            <SC_CHECK ok={rr.company_informations.receivability === true} label={i18next.t("light.docViewer.sc.companyInfo")} />
          )}
          {rr.expiration_date != null && (
            <SC_CHECK ok={rr.expiration_date.receivability === true} label={i18next.t("light.docViewer.sc.dateCheck")} />
          )}
        </>
      )}

      {/* Valeurs extraites */}
      {((sc.informations?.length > 0) || (sc.dates?.length > 0)) && (
        <Box style={{ marginTop: "1rem" }}>
          <Typography className={s.sectionTitle}>{i18next.t("light.docViewer.extractedValues")}</Typography>
          {[
            ...(sc.informations ?? []).map((item) => ({ label: item.label ?? item.name, value: String(item.value ?? "—") })),
            ...(sc.dates ?? []).map((item) => ({ label: item.label ?? item.name, value: item.formatted ?? formatDt(item.value) ?? "—" })),
          ].map((item, i) => (
            <QARow key={i} label={item.label}>
              <QAPill value={item.value} />
            </QARow>
          ))}
        </Box>
      )}
    </>
  );
}

// ─── Tab panels ───────────────────────────────────────────────────────────────
// Map statusReason to display label+color (same logic as multi-evidence chips)
function resolveStatusReason(statusReason) {
  if (!statusReason) return null;
  if (["BELOW_EXPECTATION_CLIENT_REFUS", "BELOW_EXPECTATION_EXTRACTOR_REFUS"].includes(statusReason))
    return { label: i18next.t("light.docViewer.insufficient"), color: "#6D00CC" };
  if (statusReason === "SMART_CONTROL_INVALIDATION")
    return { label: i18next.t("light.docViewer.invalidatedBySmartCheck"), color: "#DC2626" };
  if (["REFUS_CLIENT", "REFUS_SUPPORT", "SUPPRESSION_SUPPORT", "CONTROLES_INVALIDES"].includes(statusReason))
    return { label: i18next.t("light.docViewer.rejected"), color: "#DC2626" };
  if (statusReason === "EXPIRATION")
    return { label: i18next.t("light.docViewer.expired"), color: "#B45309" };
  if (["VALIDATION_CLIENT", "AUTOVALIDATION", "CONTROLES_VALIDES", "SMART_CONTROL_VALIDATION", "EXTRACTOR_VALIDATION"].includes(statusReason))
    return { label: i18next.t("light.docViewer.validated"), color: "#15803D" };
  return null;
}

function TabInfo({ detail, status, smartControl, classes: s, evidenceStatusReason }) {
  // Prefer evidence-specific statusReason over global file status
  const reasonCfg = resolveStatusReason(evidenceStatusReason);
  const statusCfg = reasonCfg ?? STATUS_MAP[status?.status] ?? null;
  return (
    <>
      {/* Métadonnées */}
      <Typography className={s.sectionTitle}>{i18next.t("light.docViewer.file")}</Typography>
      {detail?.uploadDate && <INFO_ROW label={i18next.t("light.docViewer.depositDate")} value={formatDt(detail.uploadDate)} />}
      <INFO_ROW label={i18next.t("light.docViewer.validUntil")} value={formatDt(detail?.expirationDate ?? status?.endDate) ?? "—"} />
      {detail?.transmitter != null && (
        <INFO_ROW label={i18next.t("light.docViewer.depositor")} value={detail.metadata?.depositor ?? (typeof detail.transmitter === "string" ? detail.transmitter : detail.transmitter?.name) ?? "—"} />
      )}
      {detail?.format && detail.format !== "NA" && (
        <INFO_ROW label={i18next.t("light.docViewer.format")} value={i18next.t(`viewer.format.${detail.format}`, detail.format)} />
      )}
      {detail?.issuer && detail.issuer !== "NA" && (
        <INFO_ROW label={i18next.t("light.docViewer.emittedBy")} value={formatIssuer(detail.issuer, detail.metadata?.fiscale_mere_fille)} />
      )}

      {/* Statut */}
      {statusCfg && (
        <>
          <Typography className={s.sectionTitle} style={{ marginTop: "1.25rem" }}>{i18next.t("light.docViewer.status")}</Typography>
          <INFO_ROW label={i18next.t("light.docViewer.state")} value={statusCfg.label} valueColor={statusCfg.color} />
          {!reasonCfg && status?.statusReasonDetail && <INFO_ROW label={i18next.t("light.docViewer.detail")} value={status.statusReasonDetail} />}
        </>
      )}

      {/* SmartCheck */}
      <SmartCheckSection sc={smartControl} classes={s} />
    </>
  );
}

function TabCollected({ detail, extraction, smartControl, classes: s }) {
  const hasAny =
    detail?.metadata?.effectif_urssaf ||
    detail?.metadata?.masse_salariale_urssaf ||
    detail?.metadata?.derniere_periode_analysee_urssaf ||
    detail?.specificData ||
    smartControl?.extractedSummary ||
    extraction?.content?.extractedData?.length > 0;

  if (!hasAny) return <Box className={s.emptyState}>{i18next.t("light.docViewer.noData")}</Box>;

  return (
    <>
      {/* Smart control summary */}
      {smartControl?.extractedSummary && (
        <>
          <Typography className={s.sectionTitle}>{i18next.t("light.docViewer.summary")}</Typography>
          <Box className={s.summaryBox}>
            <ReactMarkdown>{smartControl.extractedSummary}</ReactMarkdown>
          </Box>
        </>
      )}

      {/* AI extraction */}
      {extraction?.content?.extractedData?.length > 0 && (
        <>
          <Typography className={s.sectionTitle} style={{ marginTop: "1.25rem" }}>{i18next.t("light.docViewer.aiExtraction")}</Typography>
          {extraction.content.extractedData.map((item, i) => (
            <Box key={i} className={s.collectedRow}>
              <Typography className={s.collectedKey}>{item.label ?? item.key ?? `${i18next.t("light.docViewer.field")} ${i + 1}`}</Typography>
              <Typography className={s.collectedVal}>{item.value ?? "—"}</Typography>
            </Box>
          ))}
        </>
      )}

      {/* Workforce (URSSAF) */}
      {(detail?.metadata?.effectif_urssaf || detail?.metadata?.masse_salariale_urssaf) && (
        <>
          <Typography className={s.sectionTitle} style={{ marginTop: "1.25rem" }}>{i18next.t("light.docViewer.urssafData")}</Typography>
          {detail.metadata.effectif_urssaf && (
            <Box className={s.collectedRow}>
              <Typography className={s.collectedKey}>{i18next.t("light.docViewer.workforce")}</Typography>
              <Typography className={s.collectedVal}>{detail.metadata.effectif_urssaf}</Typography>
            </Box>
          )}
          {detail.metadata.masse_salariale_urssaf && (
            <Box className={s.collectedRow}>
              <Typography className={s.collectedKey}>{i18next.t("light.docViewer.payroll")}</Typography>
              <Typography className={s.collectedVal}>{detail.metadata.masse_salariale_urssaf}</Typography>
            </Box>
          )}
          {detail.metadata.derniere_periode_analysee_urssaf && (
            <Box className={s.collectedRow}>
              <Typography className={s.collectedKey}>{i18next.t("light.docViewer.lastAnalyzedPeriod")}</Typography>
              <Typography className={s.collectedVal}>{detail.metadata.derniere_periode_analysee_urssaf}</Typography>
            </Box>
          )}
        </>
      )}

    </>
  );
}

function TabAnswers({ formdata, classes: s }) {
  const t = (fd) => (fd.type ?? "").toLowerCase();
  const items = (formdata ?? []).filter((fd) => {
    if (t(fd) === "bic") return false;
    if (fd.value == null || fd.value === "") return false;
    if (Array.isArray(fd.value) && fd.value.length === 0) return false;
    return true;
  });
  if (items.length === 0) return <Box className={s.emptyState}>{i18next.t("light.docViewer.noAnswers")}</Box>;

  const shortTitle = (fd) => {
    const raw = fd.title ?? fd.labelFr ?? fd.label ?? fd.type ?? "";
    return raw.length > 130 ? raw.slice(0, 130).trimEnd() + "…" : raw;
  };

  const renderValue = (fd) => {
    const v = fd.value;
    if (v == null) return <Typography className={s.collectedVal}>—</Typography>;

    // Tableau d'objets (ex: choices → bénéficiaires, contacts)
    if (Array.isArray(v)) {
      return (
        <Box style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "0.3rem" }}>
          {v.map((item, idx) => (
            <Box key={idx} style={{ backgroundColor: "#F9FAFB", borderRadius: "8px", padding: "0.6rem 0.75rem", border: "1px solid #E5E7EB" }}>
              {typeof item === "object"
                ? Object.entries(item).filter(([, val]) => val != null && val !== "").map(([k, val]) => (
                    <Box key={k} style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
                      <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family, textTransform: "uppercase", letterSpacing: "0.05em", minWidth: "110px", flexShrink: 0, paddingTop: "1px" }}>{k}</Typography>
                      <Typography style={{ fontSize: "0.78rem", color: "#111827", fontFamily: "Inter", fontWeight: 500 }}>{String(val)}</Typography>
                    </Box>
                  ))
                : <Typography className={s.collectedVal}>{String(item)}</Typography>
              }
            </Box>
          ))}
        </Box>
      );
    }

    // Scalaires
    const tp = t(fd);
    let display = String(v);
    if (tp === "decimal" || tp === "currency") {
      const n = parseFloat(v);
      display = isNaN(n) ? v : n.toLocaleString("fr-FR", { maximumFractionDigits: 2 }) + (tp === "currency" ? " €" : "");
    } else if (tp === "checkbox") {
      display = v === "true" ? i18next.t("yes") : i18next.t("no");
    }
    return (
      <Box style={{ display: "inline-flex" }}>
        <Typography style={{
          fontSize: "0.82rem", fontWeight: 700, color: BRAND.textMain,
          fontFamily: "Inter", backgroundColor: BRAND.background,
          borderRadius: "6px", padding: "2px 10px",
          border: `1px solid ${BRAND.border}`,
        }}>{display}</Typography>
      </Box>
    );
  };

  return (
    <>
      <Typography className={s.sectionTitle}>{i18next.t("light.docViewer.thirdpartyAnswers")}</Typography>
      {items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((fd, i) => (
        <Box key={i} style={{ marginBottom: "0.75rem", borderBottom: `1px solid ${BRAND.border}`, paddingBottom: "0.75rem" }}>
          {/* Question */}
          <Typography style={{
            ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family,
            marginBottom: "0.35rem",
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {shortTitle(fd)}
          </Typography>
          {/* Réponse */}
          {renderValue(fd)}
        </Box>
      ))}
    </>
  );
}

const INVALID_STATUS_REASONS = ["REFUS_CLIENT", "REFUS_SUPPORT", "INTERRUPTION_TIERS", "EXPIRATION", "CONTROLES_INVALIDES", "MISE_A_JOUR_DECLARANT", "MISE_A_JOUR_SUPPORT"];
const STATUS_REASON_LABELS = {
  REFUS_CLIENT: () => i18next.t("light.docViewer.statusReason.refusClient"),
  REFUS_SUPPORT: () => i18next.t("light.docViewer.statusReason.refusSupport"),
  INTERRUPTION_TIERS: () => i18next.t("light.docViewer.statusReason.interruptionTiers"),
  EXPIRATION: () => i18next.t("light.docViewer.statusReason.expiration"),
  CONTROLES_INVALIDES: () => i18next.t("light.docViewer.statusReason.controlesInvalides"),
  MISE_A_JOUR_DECLARANT: () => i18next.t("light.docViewer.statusReason.majDeclarant"),
  MISE_A_JOUR_SUPPORT: () => i18next.t("light.docViewer.statusReason.majSupport"),
};

function getDepositorName(h) {
  if (h.issuer === "ALTARES") return h.transmitter ?? null;
  if (h.metadata?.depositor) return h.metadata.depositor;
  if (h.submitterName) return h.submitterName;
  return h.transmitter ?? null;
}

function TabHistory({ history, onViewVersion, classes: s }) {
  if (!history?.length) return <Box className={s.emptyState}>{i18next.t("light.docViewer.noHistory")}</Box>;

  // Build unified timeline: deposits + deduped requests, sorted desc
  const deposits = history.map((h) => ({
    type: "deposit",
    startDate: h.uploadDate,
    endDate: h.expirationDate,
    depositor: getDepositorName(h),
    statusReason: h.statusReason,
    statusReasonDetail: h.statusReasonDetail,
    statusActorEmail: h.statusActorEmail,
    statusCreationDate: h.statusCreationDate,
    responseId: h.responseId,
  }));

  const seen = new Set();
  const requests = history
    .filter((h) => h.requestDate)
    .filter((h) => {
      if (seen.has(h.requestDate)) return false;
      seen.add(h.requestDate);
      return true;
    })
    .map((h) => ({
      type: "request",
      startDate: h.requestDate,
      endDate: h.requestEndDate,
    }));

  const items = [...deposits, ...requests].sort(
    (a, b) => new Date(b.startDate) - new Date(a.startDate)
  );

  return (
    <Box className={s.historyTimeline}>
      {items.map((item, i) => (
        <Box key={i} className={s.historyItem}>
          {/* Dot + vertical line */}
          <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", marginRight: "0.75rem", paddingTop: "3px" }}>
            <Box className={s.historyDot} style={item.type === "deposit" ? { backgroundColor: BRAND.primary } : {}} />
            {i < items.length - 1 && <Box style={{ width: "2px", flex: 1, minHeight: "20px", backgroundColor: BRAND.border, marginTop: "4px" }} />}
          </Box>

          {/* Content */}
          <Box style={{ flex: 1, paddingBottom: "1rem" }}>
            {/* Date line */}
            <Typography style={{ fontSize: "0.82rem", fontWeight: 600, color: BRAND.textMain, fontFamily: "Inter" }}>
              {i18next.t("light.docViewer.fromDate") + " " + formatDt(item.startDate)}
              {item.endDate ? " " + i18next.t("light.docViewer.toDate") + " " + formatDt(item.endDate) : ""}
            </Typography>

            {/* Deposit: depositor name + eye */}
            {item.type === "deposit" && (
              <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "2px" }}>
                <Typography style={{ fontSize: "0.78rem", color: BRAND.textMuted, fontFamily: "Inter", flex: 1 }}>
                  {item.depositor ? i18next.t("light.docViewer.depositedBy") + " " + item.depositor : i18next.t("light.docViewer.deposited")}
                </Typography>
                {item.responseId && onViewVersion && (
                  <button
                    onClick={() => onViewVersion(item.responseId, item.startDate)}
                    title={i18next.t("light.docViewer.viewVersion")}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 4px", borderRadius: "6px", color: BRAND.textMuted, display: "flex", alignItems: "center", flexShrink: 0 }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = BRAND.background; e.currentTarget.style.color = BRAND.primary; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = BRAND.textMuted; }}
                  >
                    <VisibilityOutlinedIcon style={{ fontSize: "14px" }} />
                  </button>
                )}
              </Box>
            )}

            {/* Request */}
            {item.type === "request" && (
              <Typography style={{ fontSize: "0.78rem", color: BRAND.textMuted, fontFamily: "Inter", marginTop: "2px" }}>
                {i18next.t("light.docViewer.documentRequested")}
              </Typography>
            )}

            {/* Status reason */}
            {item.type === "deposit" && INVALID_STATUS_REASONS.includes(item.statusReason) && (
              <Box style={{ marginTop: "0.35rem" }}>
                <Box style={{ display: "inline-flex", alignItems: "center", borderRadius: "12px", padding: "2px 8px", fontSize: "0.68rem", fontWeight: 700, fontFamily: "Inter", backgroundColor: "#FEF2F2", color: "#B91C1C" }}>
                  {STATUS_REASON_LABELS[item.statusReason]?.() ?? item.statusReason}
                </Box>
                {item.statusActorEmail && (
                  <Typography style={{ fontSize: "0.72rem", color: BRAND.textMuted, fontFamily: "Inter", marginTop: "2px" }}>
                    {i18next.t("light.docViewer.by")} {item.statusActorEmail}{item.statusCreationDate ? " " + i18next.t("light.docViewer.onDate") + " " + formatDt(item.statusCreationDate) : ""}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function TabNotes({ notes, noteText, setNoteText, handleAddNote, handleDeleteNote, savingNote, thirdpartyId, accountUsers, canCreateDocNotes, canViewDocNotes, classes: s }) {
  const mention = useMentionAutocomplete(accountUsers, noteText, setNoteText);

  const onSave = () => {
    handleAddNote(mention.selectedMentions);
    mention.resetMentions();
  };

  return (
    <>
      {canCreateDocNotes && thirdpartyId && (
        <Box className={s.noteInput}>
          <Box className={s.mentionWrap} style={{ flex: 1 }}>
            {mention.mentionQuery !== null && mention.filteredUsers.length > 0 && (
              <Box className={s.mentionDropdown} ref={mention.dropdownRef}>
                {mention.filteredUsers.map((u, idx) => {
                  const full = `${u.firstname ?? ""} ${u.name ?? ""}`.trim();
                  const initials = [u.firstname?.[0], u.name?.[0]].filter(Boolean).join("").toUpperCase();
                  return (
                    <Box
                      key={u.userId ?? idx}
                      className={`${s.mentionItem} ${idx === mention.mentionIndex ? s.mentionItemActive : ""}`}
                      onMouseDown={(e) => { e.preventDefault(); mention.insertMention(u); }}
                    >
                      <Box className={s.mentionAvatar}>{initials}</Box>
                      <Typography className={s.mentionName}>{full}</Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
            <textarea
              ref={mention.textareaRef}
              className={s.noteTextarea}
              style={{ width: "100%" }}
              placeholder={i18next.t("light.detail.addNote")}
              value={noteText}
              onChange={mention.handleTextChange}
              onKeyDown={(e) => mention.handleKeyDown(e, onSave)}
              rows={2}
            />
          </Box>
          <button className={s.noteSendBtn} onClick={onSave} disabled={!noteText.trim() || savingNote}>
            <SendIcon style={{ fontSize: "14px" }} />
          </button>
        </Box>
      )}
      {canViewDocNotes && (notes.length === 0 ? (
        <Box className={s.emptyState}>{i18next.t("light.docViewer.noNote")}</Box>
      ) : (
        [...notes].reverse().map((note) => (
          <Box key={note.noteOId} className={s.noteItem}>
            <Box className={s.noteHeader}>
              <Box>
                <Typography className={s.noteAuthor}>{note.prenomNom}</Typography>
                <Typography className={s.noteDate}>{formatDt(note.dateCreation)}</Typography>
              </Box>
              {thirdpartyId && (
                <button className={s.noteDeleteBtn} onClick={() => handleDeleteNote(note.noteOId)}>
                  <DeleteOutlineIcon style={{ fontSize: "13px" }} />
                </button>
              )}
            </Box>
            <Typography
              className={s.noteText}
              dangerouslySetInnerHTML={{
                __html: (note.contenu ?? note.content ?? "").split(" ").map((w) =>
                  w.startsWith("@")
                    ? `<span style="color:#003A22;background:#CEF4C3;padding:2px 6px;border-radius:12px;font-weight:700">${w}</span>`
                    : w
                ).join(" "),
              }}
            />
          </Box>
        ))
      ))}
    </>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function LightDocViewer({ open, onClose, reponseId, fileId, dossierId, thirdpartyId, docName, typeDocumentId, acquisition, evidences: initialEvidences, onStatusChanged }) {
  const { classes: s, cx } = useStyles();
  const [state, dispatch] = useContext(Context);
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);
  const canAnswer  = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_ANSWER);
  const canApprove = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_APPROVE);
  const canReject  = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOCUMENT_REJECT);
  const canViewDocNotes   = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOCUMENT_NOTES);
  const canCreateDocNotes = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_DOCUMENT_NOTES);

  const [activeTab, setActiveTab] = useState("info");
  // Mobile only: which pane is visible (default: document so PDF is shown first)
  const [mobileView, setMobileView] = useState("document");
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);
  const [notes, setNotes] = useState([]);
  const [extraction, setExtraction] = useState(null);
  const [smartControl, setSmartControl] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [baseWidth, setBaseWidth] = useState(700);
  const pdfPaneRef = useRef(null);
  const pageRefs = useRef([]);
  const [loading, setLoading] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [accountUsers, setAccountUsers] = useState([]);
  const [historicVersion, setHistoricVersion] = useState(null); // { date } when viewing an old version

  // Multiple evidences
  const [multiEvidences, setMultiEvidences] = useState([]);
  const [activeReponseId, setActiveReponseId] = useState(reponseId);
  const [, setActiveFileId] = useState(fileId);

  // Approve / Reject
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveComment, setApproveComment] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectShare, setRejectShare] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const accountId = state.account?.id;

  // Load a single evidence by its reponseId/fileId
  const loadEvidence = useCallback(async (rId, fId) => {
    if (!accountId || !rId) return;
    setLoading(true);
    setPdfUrl(null);
    setNumPages(null);
    setCurrentPage(1);
    setZoom(1);
    setDetail(null);
    setStatus(null);
    setHistoricVersion(null);
    setHistoryItems([]);
    setNotes([]);
    setExtraction(null);
    setSmartControl(null);
    setActiveTab("info");

    try {
      const [detailRes, statusRes, notesRes] = await Promise.all([
        getEvidenceDetail(accountId, rId, dispatch),
        getFileStatus(accountId, rId, dispatch),
        thirdpartyId ? getAllNoteDocument(accountId, thirdpartyId, rId, dispatch) : Promise.resolve({ content: [] }),
      ]);

      const d = detailRes?.content;
      setDetail(d);
      setStatus(statusRes?.content);
      setNotes(notesRes?.content ?? []);

      const fid = fId ?? d?.id;
      await Promise.allSettled([
        dossierId && d?.typeDocumentId
          ? getDocumentHistoric(accountId, dossierId, d.typeDocumentId, dispatch)
              .then((r) => setHistoryItems(Array.isArray(r) ? r : []))
          : Promise.resolve(),
        getEvidenceExtractionDetail(accountId, rId, dispatch)
          .then((r) => setExtraction(r)),
        fid
          ? getEvidenceSmartControlDetail(accountId, fid, dispatch)
              .then((r) => setSmartControl(r?.content))
          : Promise.resolve(),
        fid
          ? getEvidenceFile(accountId, fid, dispatch)
              .then(({ arrayBuffer }) => {
                const blob = new Blob([arrayBuffer], { type: "application/pdf" });
                setPdfUrl(URL.createObjectURL(blob));
              })
          : Promise.resolve(),
      ]);
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setLoading(false);
    }
  }, [accountId, dossierId, thirdpartyId, dispatch]);

  const load = useCallback(async () => {
    if (!open || !reponseId || !accountId) return;
    setActiveReponseId(reponseId);
    setActiveFileId(fileId);
    setMultiEvidences([]);

    // Load initial evidence
    await loadEvidence(reponseId, fileId);

    // Check for multiple evidences
    if (dossierId && typeDocumentId) {
      try {
        const res = await getEvidenceMultiple(accountId, reponseId, dossierId, typeDocumentId, dispatch);
        const list = res?.content ?? [];
        if (list.length > 1) {
          setMultiEvidences(list.map((e) => ({
            reponseId: e.responseId,
            fileId: e.id,
            uploadDate: e.uploadDate,
            expirationDate: e.expirationDate,
            statusReason: e.statusReason,
          })));
        }
      } catch (_) { /* pas de multi → ignore */ }
    } else if (initialEvidences?.length > 1) {
      // Fallback: use evidences passed as prop
      setMultiEvidences(initialEvidences.map((e) => ({
        reponseId: e.responseId,
        fileId: e.id,
        uploadDate: e.uploadDate,
        expirationDate: e.expirationDate,
        statusReason: e.statusReason,
      })));
    }
  }, [open, reponseId, accountId, fileId, dossierId, typeDocumentId, initialEvidences, loadEvidence, dispatch]);

  useEffect(() => {
    if (open) {
      load();
      if (accountId && !accountUsers.length) {
        getAccountUsers(accountId, dispatch).then((r) => setAccountUsers(r?.content ?? []));
      }
    }
    return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, load]);

  const handleSwitchEvidence = (ev) => {
    if (ev.reponseId === activeReponseId) return;
    setActiveReponseId(ev.reponseId);
    setActiveFileId(ev.fileId);
    loadEvidence(ev.reponseId, ev.fileId);
  };

  // Sync multi-evidence chip after status change
  const updateMultiStatus = (rId, newStatusReason) => {
    setMultiEvidences((prev) => prev.map((ev) =>
      ev.reponseId === rId ? { ...ev, statusReason: newStatusReason } : ev
    ));
  };

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      await approveDocument(accountId, activeReponseId, { comment: approveComment.trim() || undefined }, dispatch);
      dispatch({ type: "ALERT", alert: { type: SUCCESS, msg: i18next.t("light.docViewer.documentApproved") } });
      setApproveOpen(false);
      setApproveComment("");
      const res = await getFileStatus(accountId, activeReponseId, dispatch);
      setStatus(res?.content);
      updateMultiStatus(activeReponseId, res?.content?.statusReason ?? "VALIDATION_CLIENT");
      onStatusChanged?.();
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectComment.trim()) return;
    setActionLoading(true);
    try {
      await rejectDocument(accountId, activeReponseId, {
        comment: rejectComment.trim(),
        shareRejectionReason: rejectShare,
      }, dispatch);
      dispatch({ type: "ALERT", alert: { type: SUCCESS, msg: i18next.t("light.docViewer.documentRejected") } });
      setRejectOpen(false);
      setRejectComment("");
      setRejectShare(false);
      const res = await getFileStatus(accountId, activeReponseId, dispatch);
      setStatus(res?.content);
      updateMultiStatus(activeReponseId, res?.content?.statusReason ?? "REFUS_CLIENT");
      onStatusChanged?.();
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddNote = async (selectedMentions = []) => {
    if (!noteText.trim() || !thirdpartyId) return;
    setSavingNote(true);
    try {
      const mentionRegexp = /@([\w\u00C0-\u017E]+ [\w\u00C0-\u017E]+)/g;
      const matched = [...noteText.matchAll(mentionRegexp)].map((m) => m[1]);
      const filteredUsers = selectedMentions
        .filter((m) => matched.includes(m.nomPrenom))
        .map((m) => m.userOid);
      await createNoteDocument(accountId, thirdpartyId, reponseId, {
        content: noteText.trim(),
        selectedUsersIds: filteredUsers,
      }, dispatch);
      setNoteText("");
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setSavingNote(false);
    }
    // Refresh hors try/catch
    try {
      const res = await getAllNoteDocument(accountId, thirdpartyId, reponseId, dispatch);
      setNotes(res?.content ?? []);
    } catch (_) { /* silent */ }
  };

  const handleDeleteNote = async (noteOId) => {
    try {
      await deleteNoteDocument(accountId, thirdpartyId, reponseId, noteOId, dispatch);
      setNotes((prev) => prev.filter((n) => n.noteOId !== noteOId));
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    }
  };

  const title = (detail?.documentCode && getDoctypeTranslatedName(state.doctypeTraductions, detail.documentCode, state.lang))
    || detail?.translatedName || docName || i18next.t("light.docViewer.document");
  const statusCfg = STATUS_MAP[status?.status] ?? { label: "—", bg: "rgba(255,255,255,0.15)", color: BRAND.white };

  // Determine which tabs are visible + their badges
  const hasAnswers = detail?.formdata?.some((fd) => fd.value != null && fd.type !== "BIC" && fd.value !== "");
  const hasCollected =
    detail?.metadata?.effectif_urssaf ||
    detail?.metadata?.masse_salariale_urssaf ||
    smartControl?.extractedSummary ||
    extraction?.content?.extractedData?.length > 0 ||
    detail?.specificData;

  const tabBadges = {
    history: historyItems.length || null,
    notes: notes.length || null,
  };

  const handleViewVersion = useCallback(async (responseId, date) => {
    if (!accountId) return;
    try {
      const detailRes = await getEvidenceDetail(accountId, responseId, dispatch);
      const fid = detailRes?.content?.id;
      if (!fid) return;
      const { arrayBuffer } = await getEvidenceFile(accountId, fid, dispatch);
      const blob = new Blob([arrayBuffer], { type: "application/pdf" });
      setPdfUrl(URL.createObjectURL(blob));
      setNumPages(null);
      setCurrentPage(1);
      setHistoricVersion({ date });
    } catch (e) {
      console.error("handleViewVersion", e);
    }
  }, [accountId, dispatch]);

  const visibleTabs = TABS.filter((t) => {
    if (t.id === "answers") return hasAnswers;
    if (t.id === "collected") return hasCollected;
    return true;
  });

  return (
    <>
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ className: s.drawerPaper, elevation: 0 }}
    >
      {/* ── Header ── */}
      <Box className={s.header}>
        <Box className={s.headerIcon}>
          <ArticleOutlinedIcon style={{ fontSize: "18px", color: BRAND.white }} />
        </Box>
        <Typography className={s.headerTitle}>{title}</Typography>
        {status?.status && (
          <Box className={s.headerStatus} style={{ backgroundColor: statusCfg.bg, color: statusCfg.color }}>
            {statusCfg.label}
          </Box>
        )}
        <button className={s.closeBtn} onClick={onClose}>
          <CloseIcon style={{ fontSize: "16px" }} />
        </button>
      </Box>

      {/* ── Mobile-only switcher: Document | Infos ── */}
      <Box className={s.mobileSwitcher}>
        <button
          className={cx(s.mobileSwitcherBtn, mobileView === "document" && s.mobileSwitcherBtnActive)}
          onClick={() => setMobileView("document")}
        >
          <ArticleOutlinedIcon style={{ fontSize: "15px" }} />
          {i18next.t("light.docViewer.document") || "Document"}
        </button>
        <button
          className={cx(s.mobileSwitcherBtn, mobileView === "details" && s.mobileSwitcherBtnActive)}
          onClick={() => setMobileView("details")}
        >
          <InfoOutlinedIcon style={{ fontSize: "15px" }} />
          {i18next.t("light.docViewer.details") || "Infos"}
        </button>
      </Box>

      {/* ── Body ── */}
      <Box className={s.body}>

        {/* ── Sidebar ── */}
        <Box className={cx(s.sidebar, mobileView !== "details" && s.mobileHidden)}>
          {/* Tab bar */}
          <Box className={s.tabBar}>
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              const badge = tabBadges[tab.id];
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  className={cx(s.tab, isActive && s.tabActive)}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon style={{ fontSize: "14px", flexShrink: 0 }} />
                  {tab.label()}
                  {badge > 0 && (
                    <Box className={cx(s.tabBadge, isActive && s.tabBadgeActive)}>{badge}</Box>
                  )}
                </button>
              );
            })}
          </Box>

          {/* Multiple evidences selector */}
          {multiEvidences.length > 1 && (
            <Box style={{
              display: "flex", flexWrap: "wrap", gap: "0.35rem",
              padding: "0.6rem 1rem",
              backgroundColor: BRAND.background, borderBottom: `1px solid ${BRAND.border}`,
            }}>
              <Typography style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, width: "100%", marginBottom: "0.15rem" }}>
                {multiEvidences.length} {i18next.t("light.docViewer.filesDeposited")}
              </Typography>
              {multiEvidences.map((ev, i) => {
                const isActive = ev.reponseId === activeReponseId;
                // Status from statusReason (same logic as advanced PanelListMultiple)
                const stCfg = (() => {
                  if (!ev.statusReason) return { label: i18next.t("light.docViewer.actionRequired"), color: "#CC7B00", bg: "#FFF7ED" };
                  if (["BELOW_EXPECTATION_CLIENT_REFUS", "BELOW_EXPECTATION_EXTRACTOR_REFUS"].includes(ev.statusReason))
                    return { label: i18next.t("light.docViewer.insufficient"), color: "#6D00CC", bg: "#F3E8FF" };
                  if (ev.statusReason === "SMART_CONTROL_INVALIDATION")
                    return { label: i18next.t("light.docViewer.invalidated"), color: "#DC2626", bg: "#FEF2F2" };
                  if (["REFUS_CLIENT", "REFUS_SUPPORT", "SUPPRESSION_SUPPORT", "CONTROLES_INVALIDES"].includes(ev.statusReason))
                    return { label: i18next.t("light.docViewer.rejected"), color: "#DC2626", bg: "#FEF2F2" };
                  if (ev.statusReason === "EXPIRATION")
                    return { label: i18next.t("light.docViewer.expired"), color: "#B45309", bg: "#FEF3C7" };
                  return { label: i18next.t("light.docViewer.validated"), color: "#15803D", bg: "#DCFCE7" };
                })();
                return (
                  <button
                    key={ev.reponseId ?? i}
                    onClick={() => handleSwitchEvidence(ev)}
                    style={{
                      display: "flex", alignItems: "center", gap: "0.4rem",
                      padding: "0.35rem 0.75rem", borderRadius: RADIUS.sm,
                      border: isActive ? `1.5px solid ${BRAND.primary}` : `1px solid ${BRAND.border}`,
                      backgroundColor: isActive ? `${BRAND.primary}0D` : BRAND.white,
                      cursor: "pointer", fontFamily: FONT.family, ...FONT.small,
                      color: isActive ? BRAND.primary : BRAND.textMain,
                      fontWeight: isActive ? 700 : 500,
                      transition: "border-color 0.15s, background 0.15s",
                    }}
                  >
                    <ArticleOutlinedIcon style={{ fontSize: "13px" }} />
                    {i18next.t("light.docViewer.file")} {i + 1}
                    {ev.uploadDate && (
                      <span style={{ ...FONT.meta, color: BRAND.textMuted, fontWeight: 400 }}>
                        {new Date(ev.uploadDate).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}
                      </span>
                    )}
                    <span style={{ ...FONT.tiny, fontWeight: 700, color: stCfg.color, backgroundColor: stCfg.bg, borderRadius: "10px", padding: "1px 6px" }}>
                      {stCfg.label}
                    </span>
                  </button>
                );
              })}
            </Box>
          )}

          {/* Action banner for ACTION_REQUIRED */}
          {status?.status === "ACTION_REQUIRED" && (
            <Box style={{
              display: "flex", alignItems: "center", gap: "0.75rem",
              padding: "0.75rem 1.25rem",
              backgroundColor: "#FFF7ED", borderBottom: `1px solid #FDBA74`,
            }}>
              <Box style={{ flex: 1 }}>
                <Typography style={{ ...FONT.h3, fontFamily: FONT.family, color: "#9A3412" }}>
                  {i18next.t("light.docViewer.actionRequired")}
                </Typography>
                <Typography style={{ ...FONT.small, fontFamily: FONT.family, color: "#C2410C", marginTop: "0.15rem" }}>
                  {i18next.t("light.docViewer.actionRequiredDesc")}
                </Typography>
              </Box>
              {canReject && (
              <button
                onClick={() => setRejectOpen(true)}
                disabled={actionLoading}
                style={{
                  display: "flex", alignItems: "center", gap: "0.35rem",
                  padding: "0.45rem 1rem", borderRadius: RADIUS.sm,
                  border: `1.5px solid #DC2626`, backgroundColor: BRAND.white,
                  color: "#DC2626", fontFamily: FONT.family, ...FONT.h3,
                  cursor: "pointer", transition: "background 0.15s",
                  flexShrink: 0,
                }}
              >
                <CancelOutlinedIcon style={{ fontSize: "16px" }} />
                {i18next.t("light.docViewer.reject")}
              </button>
              )}
              {canApprove && (
              <button
                onClick={() => setApproveOpen(true)}
                disabled={actionLoading}
                style={{
                  display: "flex", alignItems: "center", gap: "0.35rem",
                  padding: "0.45rem 1rem", borderRadius: RADIUS.sm,
                  border: "none", backgroundColor: "#15803D",
                  color: BRAND.white, fontFamily: FONT.family, ...FONT.h3,
                  cursor: "pointer", transition: "opacity 0.15s",
                  flexShrink: 0, opacity: actionLoading ? 0.5 : 1,
                }}
              >
                <CheckCircleOutlineIcon style={{ fontSize: "16px" }} />
                {actionLoading ? i18next.t("light.docViewer.approving") : i18next.t("light.docViewer.approve")}
              </button>
              )}
            </Box>
          )}

          {/* Answer/Update banner for DO/DO_FO documents */}
          {(acquisition === "DO" || acquisition === "DO_FO") && status?.status !== "ACTION_REQUIRED" && (
            <Box style={{
              display: "flex", alignItems: "center", gap: "0.75rem",
              padding: "0.65rem 1.25rem",
              backgroundColor: ["IN_PROGRESS", "MISSING"].includes(status?.status) ? `${BRAND.secondary}15` : BRAND.background,
              borderBottom: `1px solid ${["IN_PROGRESS", "MISSING"].includes(status?.status) ? BRAND.secondary : BRAND.border}`,
            }}>
              <Box style={{ flex: 1 }}>
                <Typography style={{ ...FONT.small, fontFamily: FONT.family, color: BRAND.textMuted }}>
                  {["IN_PROGRESS", "MISSING"].includes(status?.status)
                    ? i18next.t("light.docViewer.respondBanner")
                    : i18next.t("light.docViewer.updateBanner")}
                </Typography>
              </Box>
              {canAnswer && (
              <button
                onClick={() => {
                  sessionStorage.setItem("light_form_return", JSON.stringify({ returnUrl: window.location.pathname, reopenDoc: { reponseId, fileId, documentName: docName, typeDocumentId, acquisition } }));
                  window.location.href = `${getEnv("REACT_APP_API_URL_FORM")}/compteDO/${state.account.id}/document/${typeDocumentId}/dossier/${dossierId}`;
                }}
                style={{
                  display: "flex", alignItems: "center", gap: "0.35rem",
                  padding: "0.4rem 1rem", borderRadius: RADIUS.sm,
                  border: "none",
                  backgroundColor: ["IN_PROGRESS", "MISSING"].includes(status?.status) ? BRAND.secondary : BRAND.primary,
                  color: ["IN_PROGRESS", "MISSING"].includes(status?.status) ? BRAND.primary : BRAND.white,
                  fontFamily: FONT.family, ...FONT.h3,
                  cursor: "pointer", flexShrink: 0,
                }}
              >
                <ReplyOutlinedIcon style={{ fontSize: "15px", transform: "scaleX(-1)" }} />
                {["IN_PROGRESS", "MISSING"].includes(status?.status) ? i18next.t("light.docViewer.respond") : i18next.t("light.docViewer.updateResponse")}
              </button>
              )}
            </Box>
          )}

          {/* Tab content */}
          <Box className={s.tabContent}>
            {loading ? (
              <Box className={s.loading}>{i18next.t("light.common.loading")}</Box>
            ) : (
              <>
                {activeTab === "info" && (
                  <TabInfo detail={detail} status={status} smartControl={smartControl} classes={s} evidenceStatusReason={multiEvidences.find((e) => e.reponseId === activeReponseId)?.statusReason} />
                )}
                {activeTab === "collected" && (
                  <TabCollected detail={detail} extraction={extraction} smartControl={smartControl} classes={s} />
                )}
                {activeTab === "answers" && (
                  <TabAnswers formdata={detail?.formdata} classes={s} />
                )}
                {activeTab === "history" && (
                  <TabHistory history={historyItems} onViewVersion={handleViewVersion} classes={s} />
                )}
                {activeTab === "notes" && (
                  <TabNotes
                    notes={notes}
                    noteText={noteText}
                    setNoteText={setNoteText}
                    handleAddNote={handleAddNote}
                    handleDeleteNote={handleDeleteNote}
                    savingNote={savingNote}
                    thirdpartyId={thirdpartyId}
                    accountUsers={accountUsers}
                    canCreateDocNotes={canCreateDocNotes}
                    canViewDocNotes={canViewDocNotes}
                    classes={s}
                  />
                )}
              </>
            )}
          </Box>
        </Box>

        {/* ── PDF pane ── */}
        <Box className={cx(s.pdfPane, mobileView !== "document" && s.mobileHidden)} ref={pdfPaneRef}>
          {loading ? (
            <Box className={s.pdfPlaceholder}>
              <ArticleOutlinedIcon style={{ fontSize: "48px" }} />
              <span>{i18next.t("light.common.loading")}</span>
            </Box>
          ) : pdfUrl ? (
            <>
              {/* ── Historic version banner ── */}
              {historicVersion && (
                <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", padding: "0.5rem 1rem", backgroundColor: "#FFF7ED", borderBottom: "1px solid #FDBA74", flexShrink: 0 }}>
                  <Box style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                    <VisibilityOutlinedIcon style={{ fontSize: "14px", color: "#C2410C" }} />
                    <Typography style={{ fontSize: "0.78rem", fontFamily: "Inter", color: "#C2410C", fontWeight: 600 }}>
                      {i18next.t("light.docViewer.versionOf")} {formatDt(historicVersion.date)}
                    </Typography>
                  </Box>
                  <button
                    onClick={() => { setHistoricVersion(null); load(); }}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: "0.72rem", fontFamily: "Inter", color: "#C2410C", textDecoration: "underline", padding: 0 }}
                  >
                    {i18next.t("light.docViewer.backToCurrentVersion")}
                  </button>
                </Box>
              )}

              {/* ── Toolbar ── */}
              <Box className={s.pdfToolbar}>
                {/* Page navigation */}
                {numPages > 1 && (
                  <Box className={s.pdfToolbarGroup}>
                    <button className={s.pdfBtn} disabled={currentPage <= 1}
                      onClick={() => {
                        const p = currentPage - 1;
                        setCurrentPage(p);
                        pageRefs.current[p - 1]?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}>
                      <ChevronLeftIcon style={{ fontSize: "18px" }} />
                    </button>
                    <Typography className={s.pdfPageInfo}>{currentPage} / {numPages}</Typography>
                    <button className={s.pdfBtn} disabled={currentPage >= numPages}
                      onClick={() => {
                        const p = currentPage + 1;
                        setCurrentPage(p);
                        pageRefs.current[p - 1]?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}>
                      <ChevronRightIcon style={{ fontSize: "18px" }} />
                    </button>
                  </Box>
                )}

                <Box className={s.pdfToolbarDivider} />

                {/* Zoom */}
                <Box className={s.pdfToolbarGroup}>
                  <button className={s.pdfBtn} disabled={zoom <= 0.5}
                    onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}>
                    <ZoomOutOutlinedIcon style={{ fontSize: "16px" }} />
                  </button>
                  <Typography className={s.pdfPageInfo}>{Math.round(zoom * 100)}%</Typography>
                  <button className={s.pdfBtn} disabled={zoom >= 2.5}
                    onClick={() => setZoom((z) => Math.min(2.5, +(z + 0.25).toFixed(2)))}>
                    <ZoomInOutlinedIcon style={{ fontSize: "16px" }} />
                  </button>
                </Box>

                <Box className={s.pdfToolbarSpacer} />

                {/* Download */}
                <button className={s.pdfBtn}
                  onClick={() => {
                    const a = document.createElement("a");
                    a.href = pdfUrl;
                    a.download = (docName ?? "document") + ".pdf";
                    a.click();
                  }}>
                  <DownloadOutlinedIcon style={{ fontSize: "16px" }} />
                  {i18next.t("light.docViewer.download")}
                </button>
              </Box>

              {/* ── Pages ── */}
              <Box className={s.pdfScroll}>
                <Document
                  file={pdfUrl}
                  onLoadSuccess={({ numPages: n }) => {
                    setNumPages(n);
                    pageRefs.current = new Array(n);
                    if (pdfPaneRef.current) {
                      setBaseWidth(Math.min(pdfPaneRef.current.clientWidth - 48, 900));
                    }
                  }}
                  loading={<Box className={s.pdfPlaceholder} style={{ minHeight: "200px" }}><span>{i18next.t("light.common.loading")}</span></Box>}
                  error={<Box className={s.pdfPlaceholder}><ArticleOutlinedIcon style={{ fontSize: "40px" }} /><span>{i18next.t("light.docViewer.pdfLoadError")}</span></Box>}
                >
                  {Array.from({ length: numPages ?? 0 }, (_, i) => (
                    <Box
                      key={i}
                      ref={(el) => { pageRefs.current[i] = el; }}
                      style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.18)", borderRadius: "3px", overflow: "hidden" }}
                    >
                      <Page
                        pageNumber={i + 1}
                        width={Math.round(baseWidth * zoom)}
                        renderTextLayer
                        renderAnnotationLayer
                        onRenderSuccess={() => { if (i === 0) setCurrentPage(1); }}
                      />
                    </Box>
                  ))}
                </Document>
              </Box>
            </>
          ) : (
            <Box className={s.pdfPlaceholder}>
              <ArticleOutlinedIcon style={{ fontSize: "48px" }} />
              <span>{i18next.t("light.docViewer.previewUnavailable")}</span>
            </Box>
          )}
        </Box>

      </Box>
    </Drawer>

    {/* Approve dialog */}
    <Dialog
      open={approveOpen}
      onClose={() => { if (!actionLoading) { setApproveOpen(false); setApproveComment(""); } }}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: RADIUS.lg, fontFamily: FONT.family, overflow: "hidden" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: FONT.family, ...FONT.h2, color: BRAND.white, backgroundColor: "#15803D", pb: 1.5 }}>
        <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <CheckCircleOutlineIcon style={{ fontSize: "18px" }} />
          {i18next.t("light.docViewer.approveDocument")}
        </Box>
        <IconButton size="small" onClick={() => { if (!actionLoading) { setApproveOpen(false); setApproveComment(""); } }} sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: BRAND.white } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Box style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
        <Typography style={{ ...FONT.body, color: BRAND.textMain, fontFamily: FONT.family, lineHeight: 1.6 }}>
          {i18next.t("light.docViewer.confirmApproval")}
        </Typography>
        <Box>
          <Typography style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.35rem" }}>
            {i18next.t("light.docViewer.commentOptional")}
          </Typography>
          <textarea
            style={{ width: "100%", boxSizing: "border-box", borderRadius: RADIUS.sm, border: `1.5px solid ${BRAND.border}`, padding: "0.6rem 0.75rem", fontFamily: FONT.family, ...FONT.body, resize: "none", outline: "none", color: BRAND.textMain, lineHeight: 1.5 }}
            placeholder={i18next.t("light.detail.addComment")}
            value={approveComment}
            onChange={(e) => setApproveComment(e.target.value.slice(0, 1000))}
            rows={2}
            maxLength={1000}
          />
          <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family, textAlign: "right", marginTop: "0.2rem" }}>
            {approveComment.length}/1000
          </Typography>
        </Box>
        <Box style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", paddingTop: "0.5rem" }}>
          <button
            onClick={() => { setApproveOpen(false); setApproveComment(""); }}
            disabled={actionLoading}
            style={{ padding: "0.5rem 1.25rem", borderRadius: RADIUS.md, border: `1.5px solid ${BRAND.border}`, backgroundColor: BRAND.white, color: BRAND.textMain, fontFamily: FONT.family, ...FONT.h3, cursor: "pointer" }}
          >
            {i18next.t("cancel")}
          </button>
          <button
            onClick={handleApprove}
            disabled={actionLoading}
            style={{ padding: "0.5rem 1.25rem", borderRadius: RADIUS.md, border: "none", backgroundColor: "#15803D", color: BRAND.white, fontFamily: FONT.family, ...FONT.h3, cursor: "pointer", opacity: actionLoading ? 0.5 : 1 }}
          >
            {actionLoading ? i18next.t("light.docViewer.approving") : i18next.t("light.docViewer.approve")}
          </button>
        </Box>
      </Box>
    </Dialog>

    {/* Reject dialog */}
    <Dialog
      open={rejectOpen}
      onClose={() => { if (!actionLoading) { setRejectOpen(false); setRejectComment(""); setRejectShare(false); } }}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: RADIUS.lg, fontFamily: FONT.family, overflow: "hidden" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: FONT.family, ...FONT.h2, color: BRAND.white, backgroundColor: "#DC2626", pb: 1.5 }}>
        <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <CancelOutlinedIcon style={{ fontSize: "18px" }} />
          {i18next.t("light.docViewer.rejectDocument")}
        </Box>
        <IconButton size="small" onClick={() => { if (!actionLoading) { setRejectOpen(false); setRejectComment(""); setRejectShare(false); } }} sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: BRAND.white } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Box style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
        <Typography style={{ ...FONT.body, color: BRAND.textMain, fontFamily: FONT.family, lineHeight: 1.6 }}>
          {i18next.t("light.docViewer.rejectPrompt")}
        </Typography>
        <Box>
          <Typography style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.35rem" }}>
            {i18next.t("light.docViewer.rejectReason")}
          </Typography>
          <textarea
            style={{ width: "100%", boxSizing: "border-box", borderRadius: RADIUS.sm, border: `1.5px solid ${BRAND.border}`, padding: "0.6rem 0.75rem", fontFamily: FONT.family, ...FONT.body, resize: "none", outline: "none", color: BRAND.textMain, lineHeight: 1.5 }}
            placeholder={i18next.t("light.docViewer.rejectPlaceholder")}
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value.slice(0, 1000))}
            rows={3}
            maxLength={1000}
          />
          <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family, textAlign: "right", marginTop: "0.2rem" }}>
            {rejectComment.length}/1000
          </Typography>
        </Box>
        <Box
          style={{ display: "flex", alignItems: "center", gap: "0.35rem", cursor: "pointer" }}
          onClick={() => setRejectShare((v) => !v)}
        >
          <Checkbox checked={rejectShare} size="small" sx={{ padding: 0, color: BRAND.textMuted, "&.Mui-checked": { color: BRAND.primary } }} />
          <Typography style={{ ...FONT.small, color: BRAND.textMain, fontFamily: FONT.family }}>
            {i18next.t("light.docViewer.shareRejectReason")}
          </Typography>
        </Box>
        <Box style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", paddingTop: "0.5rem" }}>
          <button
            onClick={() => { setRejectOpen(false); setRejectComment(""); setRejectShare(false); }}
            disabled={actionLoading}
            style={{ padding: "0.5rem 1.25rem", borderRadius: RADIUS.md, border: `1.5px solid ${BRAND.border}`, backgroundColor: BRAND.white, color: BRAND.textMain, fontFamily: FONT.family, ...FONT.h3, cursor: "pointer" }}
          >
            {i18next.t("cancel")}
          </button>
          <button
            onClick={handleReject}
            disabled={actionLoading || !rejectComment.trim()}
            style={{ padding: "0.5rem 1.25rem", borderRadius: RADIUS.md, border: "none", backgroundColor: "#DC2626", color: BRAND.white, fontFamily: FONT.family, ...FONT.h3, cursor: "pointer", opacity: (actionLoading || !rejectComment.trim()) ? 0.5 : 1 }}
          >
            {actionLoading ? i18next.t("light.docViewer.sending") : i18next.t("light.docViewer.reject")}
          </button>
        </Box>
      </Box>
    </Dialog>
    </>
  );
}
